import {User} from '@hconnect/apiclient'
import {CircularProgress} from '@mui/material'
import React from 'react'
import {useFormContext} from 'react-hook-form'

import {useAdminPilotUser} from '../../../common/hooks/useAdminPilotUser'
import {
  DataScopeConfiguration,
  useRolesConfigurationForUserId
} from '../../../common/hooks/useRolesConfiguration'
import {
  DYNAMIC_SELECT_DATA_SCOPE_TYPE,
  LOOKUP_DATA_SCOPE_TYPE,
  READONLY_DATA_SCOPE_TYPE,
  TEXT_DATA_SCOPE_TYPE,
  TREE_LOOKUP_DATA_SCOPE_TYPE,
  getDataScopeConfiguration,
  sortDataScopes
} from '../../../modules/DataScopeConfiguration'
import {GroupedRoleReducerAction} from '../../../modules/GroupedRoleAssignmentReducer'
import {Permission} from '../../../modules/LoggedInUser'
import {GroupedRoleAssignment} from '../../../modules/ManageUsers.selectors'
import {RoleOptions} from '../RoleEditLayout'

import {DynamicSelectDataScope} from './DynamicSelectDataScope'
import {LookupDataScope} from './LookupDataScope'
import {PayerOrCustomerSelectionDataScope} from './PayerOrCustomerSelectionDataScope'
import {ReadonlyDataScope} from './ReadonlyDataScope'
import {TextDataScope} from './TextDataScope'
import {TreeLookupDataScope} from './TreeLookupDataScope'

type DataScopeFieldsProps = {
  dispatchRoleStateUpdate: React.Dispatch<GroupedRoleReducerAction>
  groupedRoleAssignment: GroupedRoleAssignment
  setShowCustomerDropdown: (show: boolean) => void
  showCustomerDropdown: boolean
  selectedOptions: RoleOptions
  setSelectedOptions: (options: RoleOptions) => void
  isLoadingCustomerAdminOptions: boolean
  customerAdminData?: Record<string, string | undefined>
  loggedInUserPermissions?: Permission[] | null
  loggedInUserProfile?: User | null
}

const isLookupDataScopeRequired = (
  isRequired: boolean,
  dataScopes?: DataScopeConfiguration[]
): boolean => {
  const payerIdsConfig = dataScopes?.find(
    (o) => o.dataScope === 'payerIds' && o.multiSelect === true
  )
  const customerIdsConfig = dataScopes?.find(
    (o) => o.dataScope === 'customerIds' && o.multiSelect === true
  )

  const contractIdsConfig = dataScopes?.find(
    (o) => o.dataScope === 'contractIds' && o.multiSelect === true
  )

  const isOneRequired =
    (payerIdsConfig && payerIdsConfig.isRequired) ||
    (customerIdsConfig && customerIdsConfig.isRequired)

  const payersOrCustomersIsRequired = Boolean(payerIdsConfig && customerIdsConfig && isOneRequired)

  if (!contractIdsConfig && payersOrCustomersIsRequired) {
    return false
  }

  return isRequired
}

export const DataScopeFields = ({
  dispatchRoleStateUpdate,
  groupedRoleAssignment,
  customerAdminData,
  setShowCustomerDropdown,
  showCustomerDropdown,
  selectedOptions,
  isLoadingCustomerAdminOptions,
  loggedInUserPermissions,
  loggedInUserProfile,
  setSelectedOptions
}: DataScopeFieldsProps) => {
  const {watch, formState, getValues} = useFormContext<GroupedRoleAssignment>()
  const {rolesByType} = useRolesConfigurationForUserId(loggedInUserProfile)
  const isUserAdminPilot = useAdminPilotUser(loggedInUserPermissions)

  const formValues = watch()

  if (!formValues.roleType) {
    return null
  }

  if (isLoadingCustomerAdminOptions) {
    return <CircularProgress style={{margin: '8px 0'}} />
  }

  const handleDataScopeFilter = (dataScope: string) => {
    if (isUserAdminPilot) {
      if (
        selectedOptions.countryId.isSingle &&
        selectedOptions.businessLine.isSingle &&
        selectedOptions.orgUnitId.isSingle
      ) {
        return (
          dataScope !== 'countryId' && dataScope !== 'orgUnitId' && dataScope !== 'businessLine'
        )
      }

      if (selectedOptions.countryId.isSingle && selectedOptions.businessLine.isSingle) {
        return dataScope !== 'countryId' && dataScope !== 'businessLine'
      }

      if (selectedOptions.countryId.isSingle && selectedOptions.orgUnitId.isSingle) {
        return dataScope !== 'countryId' && dataScope !== 'orgUnitId'
      }

      if (selectedOptions.countryId.isSingle) {
        return dataScope !== 'countryId'
      }
    }

    return true
  }

  const dataScopes = rolesByType?.[formValues.roleType]?.dataScopes.filter(({dataScope}) =>
    handleDataScopeFilter(dataScope)
  )

  const sortedDataScopes = dataScopes?.slice().sort(sortDataScopes)

  const roleScopeNames = new Set(
    rolesByType?.[formValues.roleType]?.dataScopes.map((d) => d.dataScope) || []
  )

  const payerIdsScope = sortedDataScopes?.find(({dataScope}) => dataScope === 'payerIds')
  const customerIdsScope = sortedDataScopes?.find(({dataScope}) => dataScope === 'customerIds')

  const hasPayerIdsData =
    groupedRoleAssignment.variantDataScopes.payerIds &&
    groupedRoleAssignment.variantDataScopes.payerIds.length > 0
  const hasCustomerScopesData =
    groupedRoleAssignment.variantDataScopes.customersScopes &&
    groupedRoleAssignment.variantDataScopes.customersScopes.length > 0

  const shouldShowCombinedComponent =
    payerIdsScope && customerIdsScope && !hasPayerIdsData && !hasCustomerScopesData

  let combinedComponentRendered = false

  return (
    <div className="flex flex-col gap-4 w-full">
      {sortedDataScopes?.map(({dataScope: dataScopeName, isRequired, multiSelect}) => {
        const configuration = getDataScopeConfiguration(
          dataScopeName,
          isUserAdminPilot,
          formValues.roleType
        )
        const fieldName = `commonDataScopes.${dataScopeName}`
        const errorMessage = formState.isSubmitted
          ? formState.errors.commonDataScopes?.[
              dataScopeName as keyof typeof formState.errors.commonDataScopes
            ]?.message
          : null

        if (
          shouldShowCombinedComponent &&
          (dataScopeName === 'payerIds' || dataScopeName === 'customerIds')
        ) {
          if (combinedComponentRendered) {
            return null
          }

          combinedComponentRendered = true

          const payerConfiguration = getDataScopeConfiguration(
            'payerIds',
            isUserAdminPilot,
            formValues.roleType
          )
          const customerConfiguration = getDataScopeConfiguration(
            'customerIds',
            isUserAdminPilot,
            formValues.roleType
          )

          return (
            <PayerOrCustomerSelectionDataScope
              key="payer-or-customer-selection"
              errorMessage={errorMessage || ''}
              dispatchRoleStateUpdate={dispatchRoleStateUpdate}
              groupedRoleAssignment={groupedRoleAssignment}
              customerAdminData={customerAdminData}
              setShowCustomerOrPayerDropdown={setShowCustomerDropdown}
              showCustomerOrPayerDropdown={showCustomerDropdown}
              loggedInUserPermissions={loggedInUserPermissions}
              payerConfiguration={payerConfiguration}
              customerConfiguration={customerConfiguration}
              isRequired={isRequired}
              multiSelect={multiSelect}
              roleDataScopeNames={roleScopeNames}
            />
          )
        }

        if (configuration.type === DYNAMIC_SELECT_DATA_SCOPE_TYPE) {
          return (
            <DynamicSelectDataScope
              key={fieldName}
              errorMessage={errorMessage}
              configuration={configuration}
              dataScopeName={dataScopeName}
              isRequired={isRequired}
              dispatchRoleStateUpdate={dispatchRoleStateUpdate}
              multiSelect={multiSelect}
              shouldDisplayAllInDropdown={false}
              selectedOptions={selectedOptions}
              groupedRoleAssignment={groupedRoleAssignment}
              loggedInUserPermissions={loggedInUserPermissions}
              setSelectedOptions={setSelectedOptions}
            />
          )
        }

        if (configuration.type === LOOKUP_DATA_SCOPE_TYPE) {
          return (
            <LookupDataScope
              key={fieldName}
              errorMessage={errorMessage || ''}
              configuration={configuration}
              groupedRoleAssignment={groupedRoleAssignment}
              dataScopeName={dataScopeName}
              isRequired={isLookupDataScopeRequired(isRequired, dataScopes)}
              dispatchRoleStateUpdate={dispatchRoleStateUpdate}
              multiSelect={multiSelect}
              customerAdminData={customerAdminData}
              setShowCustomerOrPayerDropdown={setShowCustomerDropdown}
              showCustomerOrPayerDropdown={showCustomerDropdown}
              loggedInUserPermissions={loggedInUserPermissions}
              roleDataScopeNames={roleScopeNames}
            />
          )
        }
        if (configuration.type === TREE_LOOKUP_DATA_SCOPE_TYPE) {
          return (
            <TreeLookupDataScope
              key={fieldName}
              dataScopeName={dataScopeName}
              errorMessage={errorMessage || ''}
              configuration={configuration}
              dispatchRoleStateUpdate={dispatchRoleStateUpdate}
            />
          )
        }
        if (configuration.type === TEXT_DATA_SCOPE_TYPE) {
          return (
            <TextDataScope
              key={fieldName}
              errorMessage={errorMessage || ''}
              dataScopeName={dataScopeName}
              multiSelect={multiSelect}
              isRequired={isRequired}
              dispatchRoleStateUpdate={dispatchRoleStateUpdate}
            />
          )
        }

        if (configuration.type === READONLY_DATA_SCOPE_TYPE) {
          return (
            <ReadonlyDataScope
              key={fieldName}
              errorMessage={errorMessage || ''}
              dataScopeName={dataScopeName}
              multiSelect={multiSelect}
              isRequired={isRequired}
            />
          )
        }
        return (
          <input
            type="hidden"
            key={dataScopeName}
            name={`dataScope.${dataScopeName}`}
            value={getValues().commonDataScopes[dataScopeName] || ''}
          />
        )
      })}
    </div>
  )
}
